import React, { useState, useEffect, useCallback, useRef } from "react";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import {
  PricingCard,
  PricingSection,
  SliderSection,
} from "./aragon/components/templates/ProductPageNew";
import FAQSection from "../sections/FAQSection";
import { Icon, Text,Button, Tooltip, Dialog } from "@ui-component-library";
import { useOpenSubscriptionCheckoutOptional } from "@components/Checkout/config/SubscriptionCheckout/SubscriptionCheckoutContext";
import Navigation from "../static/Navigation";
import Footer from "../static/Footer";
import { forceLoginToPurchasePlus } from "@utils/abTestCookies.js";
import { useLogin } from "../login/context/LoginContext";

const COMPARISON_TABLE_SECTION_COUNT = 9;

export default function PricingPageNew() {
  const subscription = useOpenSubscriptionCheckoutOptional();
  const { isLogged } = useLogin() 
  const isMinSkogPage =
    typeof window !== "undefined" && window.location.href.includes("min-skog");
  
  const handleOpenCheckout = useCallback(() => {
    if (!forceLoginToPurchasePlus(isLogged)) {
      subscription?.openCheckout?.();
    } else {
      window.open("/min-skog/?subscription-checkout=true", "_self");
    }
  }, [subscription?.openCheckout, isLogged]);
  const [open, setOpen] = useState<number[]>(() =>
    Array.from({ length: COMPARISON_TABLE_SECTION_COUNT }, (_, i) => i)
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  const [openPriceHelperDialog, setOpenPriceHelperDialog] = useState<{
    state: boolean;
    text: string;
    details: React.ReactNode;
  }>({ state: false, text: "", details: "" });
  const comparisonTableRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggle = (id: number) => {
    setOpen((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const iconSize = isMobile ? "medium" : "large";
  const tableData = [
    {
      title: "Sälja Virke & Biobränsle",
      rows: [
        {
          text: "Sälja virke & biobränsle",
          info: "Tillgång till Treebulas marknadsplats för slutavverkning och gallring. Konkurrensutsätt ditt virke och få bästa möjliga pris",
          bas: <Icon name="CheckSimple" size={iconSize} className="fill-[#1A1A1A]" />,
          plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />,
        },
      ],
    },
    {
      title: "Digital skogsbruksplan",
      rows: [
        { text: "Digital skogsbruksplan", bas: <Text variant="text-sm/leading-normal/normal">Grundläggande</Text>, plus: <Text variant="text-sm/leading-normal/normal">Fullständig</Text>, info: "Få en tydlig överblick över hela din fastighet med uppdaterad fakta, kartor, ekonomisk potential och mycket mer, direkt i mobilen."},
        { text: "Interaktiv skogskarta", bas: <Icon name="CheckSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Med vår interaktiva skogskarta får du en tydligare överblick av din fastighet. Klicka dig fram mellan avdelningarna för att direkt se detaljerad information om ett specifikt område."},
        { text: "Automatisk uppdatering", bas: <Icon name="CheckSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Vi uppdaterar din skogsbruksplan när ny information blir tillgänglig"},
        { text: "Redigera skogsbruksplan (rita, ändra, justera)", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Med våra enkla redigeringsverktyg kan du själv rita nya avdelningar, justera gränser och ändra information direkt i skogsbruksplanen. Du har kontrollen, vi sköter tekniken."},
        { text: "Anteckningar och beskrivningar per avdelning", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Markera händelser, planera åtgärder och anteckna direkt i kartan medan du står i skogen. Det sparar tid och gör nästa åtgärd betydligt enklare, oavsett om du ska ta dit en entreprenör eller planera själv."},
        { text: "Kartlager & ortofoto", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Alla relevanta kartlager samlade i en användarvänlig vy."},
        { text: "Skogsöverblick", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Vi sammanställer allt från fastighetsvärde till beståndsdata i en tydlig överblick. Med Treebula Plus får du de mest värdefulla insikterna och ser potentialen i din skog på ett helt nytt sätt."},
        { text: "Ladda upp skogsbruksplan (ForeStand)", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Har du redan en skogsbruksplan i ForeStand-format? Ladda upp den direkt i vårt system. Vi ser till att din befintliga avdelningsindelning och data följer med, så att du kan fortsätta ditt skogsbruk digitalt utan att behöva börja om från noll."},
        { text: "Ladda ner skogsbruksplan som PDF", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Förvandla din digitala plan till ett PDF-dokument. Perfekt att skriva ut eller dela med banken och samarbetspartners."},
      ],
    },
    {
      title: "Skogsdata & beståndsanalys",
      rows: [
        { text: "Volym, areal & bonitet", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Den senast tillgängliga informationen, så som aktuell volym, areal och bonitet per avdelning och för fastigheten"},
        { text: "Trädslags- & åldersfördelning", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Få en sammanställning över trädslagsfördelning och ålder, både per avdelning och för hela fastigheten. Enkelt, tydligt och alltid uppdaterat."},
        { text: "Huggningsklasser", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Vi analyserar dina bestånd och föreslår huggningsklasser per avdelning."},
      ],
    },
    {
      title: "Virkespriser & Värde",
      rows: [
        { text: "Verkliga virkespriser", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Unik statistik som bygger på riktiga affärer gjorda på Treebulas marknadsplats för virke."},
        { text: "Timmer- & massavedspriser", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Uppdaterad information om vad olika skogsbolag betalar för virke."},
        { text: "Virkespriskollen", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Redan fått anbud? Vi hjälper dig jämföra och göra en bra affär."},
      ],
    },
    {
      title: "Fastighetsvärdering",
      rows: [
        { text: "Fastighetsvärdering", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "En uppdaterad fastighetsvärdering baserad på senaste publika prisstatistiken och virkesförråd."},
        { text: "Historisk prisutveckling", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" />, info: "Historisk data som visar prisutvecklingen på skogsfastigheter i ditt område."},
      ],
    },
    {
      title: "Skogsövervakning",
      rows: [
        { text: "Skogsskador & risker", info: "Få en riskbedömning för skogsskador i din skog. Vi kombinerar data för brand, barkborre, väder m.m. för att ge dig en överskådlig status.", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" /> },
        { text: "Väder, vind & klimatdata", info: "Genom att analysera historiska och aktuella vinddata visar vi om det finns risk för skador.", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" /> },
        { text: "Avverkningsanmälan i närheten", info: "Ska grannen avverka? Vi visar om och var det görs anmälan i närheten.", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" /> },
      ],
    },
    {
      title: "Marknadsinformation",
      rows: [
        { text: "Marknadsinformation", info: "Håll dig uppdaterad med Treebulas unika och oberoendeanalys av marknaden, så att du kan känna dig trygg in dina beslut.", bas: <Icon name="CheckSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" /> },
        { text: "Virkesprisindikatorn", info: "Treebulas unika Virkesprisindikator ger en vägledning om vartåt virkespriserna sannolikt kan komma att röra sig framåt.", bas: <Icon name="CheckSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" /> },
      ],
    },
    {
      title: "Kolkrediter",
      rows: [
        { text: "Kolkrediter est. värde", info: "Vi analyserar och visar potentialen för kolkrediter i din skog.", bas: <Text variant="text-sm/leading-normal/normal">Grundläggande - indikation </Text>, plus: <Text variant="text-sm/leading-normal/normal">Fullständig - Se estimerat värde</Text> },
        { text: "Tjäna kolkrediter", info: "Se om din skog har potential för kolkrediter, det estimerade värdet och kom igång enkelt att tjänat kolkrediter.", bas: <Icon name="CrossSimple" size={iconSize} className="fill-[#1A1A1A]" />, plus: <Icon name="CheckSimple" size={iconSize} className="fill-purple-plum-500" /> },
      ],
    },
    {
      title: "Rådgivning och support",
      rows: [
        { text: "Support", info: "Om du behöver stöd eller vägledning finns våra rådgivare alltid redo att hjälpa dig.", bas: <Text variant="text-sm/leading-normal/normal">Endast kundtjänst</Text>, plus: <Text variant="text-sm/leading-normal/normal">Personlig rådgivning</Text> },
      ],
    },
  ];

  const basFeatures = {
    subtitle: "Detta ingår kostnadsfritt",
    info: [
      { text: "Sälja virke & biobränsle", details: "Tillgång till Treebulas marknadsplats för slutavverkning och gallring. Konkurrensutsätt ditt virke och få bästa möjliga pris."},
      { text: "Begränsad digital skogsbruksplan", details: "Du får en bra överblick över fastigheten med utvald information per avdelning."},
      { text: "Enkel kolkrediter översikt", details: "Möjlighet att tjäna pengar på kolkrediter från din skog. Vi visar om du har potential."},  
      { text: "Marknadsinformation",  details: "Genom Treebulas unika och oberoende roll på marknaden ger vi dig uppdaterad information för bättre affärer."},
    ],
  };


  const plusFeatures = {
    subtitle: "Detta ingår i Treebula Plus",
    info: [
      { text: "Fastighetesvärdering", details: "Löpande värdering av din skogsfastighet baserad på virkesförråd och aktuella marknadspriser."},
      { text: "Digital skogsbruksplan", details: "Få en tydlig överblick över hela din fastighet med uppdaterad fakta, kartor samt ekonomisk potential."},
      { text: "Virkespriser", details: "Full tillgång till Treebulas unika prisdatabas för virkespriser"},
      { text: "Sälja virke och biobränsle", details: "Tillgång till Treebulas marknadsplats för slutavverkning och gallring. Konkurrensutsätt ditt virke och få bästa möjliga pris."},
      { text: "Skogsövervakning och risker", details: "Din skog alltid under uppsikt med Treebulas stallitövervakning av din skog. Skador och risker tydligt presenterat."},
      { text: "Marknadsinformation", details: "Genom Treebulas unika och oberoende roll på marknaden ger vi dig uppdaterad information för bättre affärer."},
      { text: "Kolkrediter est. värde", details: "Se om din skog har potential för kolkrediter, det estimerade värdet och kom igång enkelt med att tjänat kolkrediter."},
    ],
  };

  const showNav = !window.location.href.includes("min-skog");
  const showFooter = showNav;

  return (
    <div>
      {showNav && <Navigation />}
      <PricingSection
        onOpenCheckout={handleOpenCheckout}
        title="Ta kontroll över värdet i din skog"
        paragraph={
          <Text  className="text-[#737373] text-center" variant="text-base/leading-normal/normal">
            Data, analys och marknadsinsikt du behöver för att fatta trygga beslut och skapa långsiktigt värde. 
            Utan bindningstider.
          </Text>
        }
        children={
          <div className="w-full flex items-center justify-center">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PricingCard title="Bas" features={basFeatures} />
              <PricingCard
                title="Treebula Plus"
                priceCardButton={{
                  state: true,
                  text: "Uppgradera till Plus",
                }}
                features={plusFeatures}
                backgroundColor="black"
              />
            </div>
          </div>
        }
      />
      <SliderSection
        onScrollToComparison={() =>
          comparisonTableRef.current?.scrollIntoView({ behavior: "smooth" })
        }
      />

       {/* Comparison table */}
       <TailwindWrapper>
            <div ref={comparisonTableRef} className="bg-[#F6F5F4]">
            <div className="pt-24 pb-12 px-6 md:px-8">
              <div className="max-w-[1320px] mx-auto">

              {/* Mobile-only comparison table */}
              <div className={`md:hidden pt-6`}>
                <div className="mb-6">
                  <Text variant="text-lg/leading-normal/medium" className="text-[#333333]">
                    Jämför funktioner
                  </Text>
                  <Text variant="text-base/leading-normal/normal" className="text-[#737373] mt-1">
                    Välj den plan som passar dig bäst.
                  </Text>
                </div>
                <div
                  className={`z-10 mb-8 grid grid-cols-2 gap-3 bg-[linear-gradient(0deg,var(--stone-light-50,#F6F5F4)_2.72%,rgba(246,245,244,0)_124.59%)] backdrop-blur-[16px]`}
                >
                  <div className="flex flex-col items-start p-4">
                    <Text variant="text-base/leading-normal/medium">Bas</Text>
                    <Text variant="text-sm/leading-normal/normal" className="text-[#1A1A1A]">0 kr/år</Text>
                    <Button variant="secondary" onClick={() => { window.open("/min-skog/", "_self"); }} className="mt-2 w-full">
                      Kom igång
                    </Button>
                  </div>
                  <div className="flex flex-col items-start p-4">
                    <Text variant="text-base/leading-normal/medium">Plus</Text>
                    <Text variant="text-sm/leading-normal/normal" className="text-[#1A1A1A]">Från {(1399).toLocaleString('sv-SE')} kr/år</Text>
                    <Button variant="primary" onClick={handleOpenCheckout} className="mt-2 w-full">
                      Uppgradera
                    </Button>
                  </div>
                </div>
                {tableData.map((section, index) => (
                  <div key={index} className="rounded-lg overflow-hidden mb-2">
                    <button
                      type="button"
                      className="w-full bg-white rounded-lg px-4 py-4 flex justify-between items-center border-b border-[#E5E5E5] text-left"
                      onClick={() => toggle(index)}
                      aria-expanded={open.includes(index)}
                    >
                      <Text variant="text-lg/leading-normal/medium" className="text-[#333333]">
                        {section.title}
                      </Text>
                      <svg
                        className={`w-5 h-5 text-cloud-1000 shrink-0 transition-transform ${open.includes(index) ? "rotate-0" : "rotate-180"}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m5 15 7-7 7 7" />
                      </svg>
                    </button>
                    {open.includes(index) && (
                      <div className="bg-[#F6F5F4] divide-y divide-[#E5E5E5]">
                        {section.rows.map((row, i) => (
                          <div
                            key={i}
                            className={`px-4 py-3 ${i % 2 === 0 ? "bg-[#F6F5F4]" : "bg-[#FBFAF9]"}`}
                          >
                            <div
                              className="relative flex items-start gap-2 mb-2 pr-8"
                              onClick={() =>
                                setOpenPriceHelperDialog({
                                  state: true,
                                  text: row.text,
                                  details: row.info,
                                })
                              }
                              onKeyDown={(e) =>
                                e.key === "Enter" &&
                                setOpenPriceHelperDialog({ state: true, text: row.text, details: row.info })
                              }
                              role="button"
                              tabIndex={0}
                            >
                              <Text variant="text-base/leading-normal/normal" className="text-[#333333] break-all flex-1 min-w-0">
                                {row.text}
                              </Text>
                              <span className="absolute top-0 right-0 rounded-full bg-[#E8E8E8] p-0.5 shrink-0">
                                <Icon name="InfoOutlined" size="small" className="fill-[#737373]" />
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex flex-col items-center">
                                <div className="flex justify-center items-center min-h-[24px]">{row.bas}</div>
                              </div>
                              <div className="flex flex-col items-center">
                                <div className="flex justify-center items-center min-h-[24px]">{row.plus}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden md:block rounded-xl">
                <table className="w-full table-fixed border-collapse comparison-table">
                  <thead>
                    <tr className="border-b border-[#E5E5E5]">
                      <th
                        className={`px-6 py-4 text-left align-top comparison-col-1 font-normal sticky ${!isMinSkogPage ? "top-16" : "top-0"} pt-6 z-[100] bg-[linear-gradient(0deg,var(--stone-light-50,#F6F5F4)_2.72%,rgba(246,245,244,0)_124.59%)] backdrop-blur-[16px]`} 
                      >
                        <div className="flex flex-col gap-2">
                          <Text variant="text-lg/leading-normal/medium" className="text-[#333333]">
                            Jämför funktioner
                          </Text>
                          <Text variant="text-base/leading-normal/normal" className="text-[#737373]">
                            Välj den plan som passar dig bäst.
                          </Text>
                        </div>
                      </th>
                      <th
                        className={`pl-3 pr-1 py-4 text-left align-top comparison-col-2 font-normal sticky ${!isMinSkogPage ? "top-16" : "top-0"} pt-6  z-[100] bg-[linear-gradient(0deg,var(--stone-light-50,#F6F5F4)_2.72%,rgba(246,245,244,0)_124.59%)] backdrop-blur-[16px]`}
                      >
                        <div className="flex flex-col items-start gap-1">
                          <Text variant="text-base/leading-normal/medium" className="text-[#333333]">Bas</Text>
                          <Text variant="text-sm/leading-normal/normal" className="text-[#1A1A1A]">0 kr/år</Text>
                          <Button variant="secondary" onClick={() => { window.open("/min-skog/", "_self"); }} className="mt-2">
                            Kom igång
                          </Button>
                        </div>
                      </th>
                      <th
                        className={`pl-1 pr-3 py-4 text-left align-top comparison-col-3 font-normal sticky ${!isMinSkogPage ? "top-16" : "top-0"} pt-6 z-[100] bg-[linear-gradient(0deg,var(--stone-light-50,#F6F5F4)_2.72%,rgba(246,245,244,0)_124.59%)] backdrop-blur-[16px]`}
                      >
                        <div className="flex flex-col items-start gap-1">
                          <Text variant="text-base/leading-normal/medium" className="text-[#333333]">Plus</Text>
                          <Text variant="text-sm/leading-normal/normal" className="text-[#1A1A1A]">Från {(1399).toLocaleString('sv-SE')} kr/år</Text>
                          <Button variant="primary" onClick={handleOpenCheckout} className="mt-2">
                            Uppgradera
                          </Button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                  {tableData.map((section, index) => (
                    <React.Fragment key={index}>
                      <tr
                        className="cursor-pointer rounded-lg"
                        onClick={() => toggle(index)}
                      >
                        <td className="px-0 py-1" colSpan={3}>
                          <div className="bg-white rounded-lg overflow-hidden px-6 py-4 flex justify-between items-center w-full border-b border-[#E5E5E5]">
                            <Text variant="text-lg/leading-normal/medium" className="text-[#333333]">
                              {section.title}
                            </Text>
                            <button type="button" className="p-1" aria-expanded={open.includes(index)}>
                              <svg
                                className={`w-5 h-5 text-cloud-1000 transition-transform ${open.includes(index) ? "rotate-0" : "rotate-180"}`}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="m5 15 7-7 7 7" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {open.includes(index) &&
                        section.rows.map((row, i) => (
                          <tr
                            className={`${i % 2 === 0 ? "bg-[#F6F5F4]" : "bg-[#FBFAF9] border-b-14 border-solid border-[#F6F5F4]"}`}
                            key={i}
                          >
                            <td className="px-6 py-3 comparison-col-1">
                            <div className="flex items-center">
                                <Tooltip
                                    aria-label={row.text}
                                    content={row.info}
                                    side="right"
                                  >
                                <div className="flex items-center gap-2">
                                  <Text variant="text-base/leading-normal/normal" className="text-[#333333] break-all">
                                    {row.text}
                                  </Text>
                                    <Icon name="InfoOutlined" size="medium" className="fill-[#1A1A1A] rounded-full p-0.5 shrink-0" />
                                </div>
                                </Tooltip>
                                </div>
                            </td>
                            <td className="pl-3 pr-1 py-3 comparison-col-2">
                              <div className="flex justify-start items-center">{row.bas}</div>
                            </td>
                            <td className="pl-1 pr-3 py-3 comparison-col-3">
                              <div className="flex justify-start items-center">{row.plus}</div>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </div>
              </div>
            </div>
            <Dialog isOpen={openPriceHelperDialog.state} size="small">
              <Dialog.Header className="justify-between items-center">
                <Text variant="text-sm/leading-normal/normal">
                  {openPriceHelperDialog?.text}
                </Text>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setOpenPriceHelperDialog({ state: false, text: "", details: "" })
                  }
                >
                  <Icon name="CrossSimple" className="color-charcoal-500" size="medium" />
                </Button>
              </Dialog.Header>
              <Dialog.Content>
                <Text variant="text-sm/leading-normal/normal">
                  {openPriceHelperDialog?.details}
                </Text>
              </Dialog.Content>
            </Dialog>
            <div className="w-full bg-[#30353A]">
              <FAQSection
                sectionName="FAQ"
                title="Vanliga frågor och svar"
                text="Allt du behöver veta om Treebula."
                template="GeneralFAQ"
                backgroundColor="#30353A"
                textColor="#FBFBFB"
                subHeaderColor="#ECFF8C"
                mutedTextColor="#F5F5F5"
                underlineAccordion={false}
              />
            </div>
            </div>
          </TailwindWrapper>
      {showFooter && <Footer />}
      </div>
  );
}
